import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
  type ChartOptions,
  type ChartData,
} from "chart.js";
import { Line, Bar, Doughnut, Radar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
);

const LIGHT = { grid: "rgba(0,0,0,.06)", tick: "#999999" };
const DARK = { grid: "rgba(255,255,255,.08)", tick: "#777777" };

function axes(dark: boolean, extra?: Partial<ChartOptions<any>["scales"]>) {
  const c = dark ? DARK : LIGHT;
  return {
    x: {
      ticks: { color: c.tick, font: { size: 10 }, maxTicksLimit: 6 },
      grid: { color: c.grid },
      border: { display: false },
    },
    y: {
      ticks: { color: c.tick, font: { size: 10 }, maxTicksLimit: 5 },
      grid: { color: c.grid },
      border: { display: false },
    },
    ...extra,
  } as ChartOptions<any>["scales"];
}

const baseOptions = (dark: boolean, opts?: Partial<ChartOptions<any>>): ChartOptions<any> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, ...opts?.plugins },
  scales: axes(dark, opts?.scales as any),
  ...opts,
});

export interface NetivChartProps {
  data: ChartData<any>;
  dark?: boolean;
  /** Pass-through Chart.js options, merged over the NETIV defaults. */
  options?: Partial<ChartOptions<any>>;
}

export function LineChart({ data, dark = false, options }: NetivChartProps) {
  return <Line data={data} options={baseOptions(dark, options)} />;
}

export function BarChart({ data, dark = false, options, stacked = false, horizontal = false }: NetivChartProps & { stacked?: boolean; horizontal?: boolean }) {
  const merged = baseOptions(dark, options);
  if (stacked) {
    (merged.scales as any).x.stacked = true;
    (merged.scales as any).y.stacked = true;
  }
  if (horizontal) merged.indexAxis = "y";
  return <Bar data={data} options={merged} />;
}

export function DoughnutChart({ data, options }: NetivChartProps) {
  return (
    <Doughnut
      data={data}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        cutout: "58%",
        ...options,
      }}
    />
  );
}

export function RadarChart({ data, dark = false, options }: NetivChartProps) {
  const c = dark ? DARK : LIGHT;
  return (
    <Radar
      data={data}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          r: {
            min: 0,
            max: 100,
            ticks: { color: c.tick, font: { size: 9 }, stepSize: 25, backdropColor: "transparent" },
            grid: { color: c.grid },
            angleLines: { color: c.grid },
            pointLabels: { color: dark ? "#9CA3AF" : "#6B7280", font: { size: 10 } },
          },
        },
        ...options,
      }}
    />
  );
}

/* ---------------------------------------------------------------- */
/* Palette + helpers shared by screen implementations                */
/* ---------------------------------------------------------------- */
export const PALETTE = {
  gold: "#C9A84C",
  goldDim: "#9A7A38",
  up: "#1a7a3f",
  down: "#c0392b",
  warn: "#d97706",
  blue: "#1a4fa0",
  purple: "#8a3fa0",
  dao: "#A855F7",
  grey: "#cccccc",
};

/** Deterministic pseudo-random in [0,1) — keeps demo charts stable across renders. */
export function rng(seed: number): number {
  const x = Math.sin(seed + 1) * 1e4;
  return x - Math.floor(x);
}
